import { showNotification } from '@mantine/notifications';
import type { EmbeddedPromptParentOut } from '@repo/api-codegen';
import { isNil } from 'lodash-es';
import { useEffect, useState } from 'react';
import { useAIEmbedded } from './useAIEmbedded';

interface IUseStreamingAIPromptArgs {
	pollingInterval?: number;
	onFinishStreaming?: (prompt: EmbeddedPromptParentOut) => void;
}

export function useStreamingAIPrompt({
	pollingInterval = 1000,
	onFinishStreaming,
}: IUseStreamingAIPromptArgs = {}) {
	const [pollingId, setPollingId] = useState<string | null>(null);
	const [activePrompt, setActivePrompt] = useState<
		EmbeddedPromptParentOut | null | undefined
	>(null);

	useAIEmbedded({
		id: pollingId!,
		options: {
			staleTime: 0,
			gcTime: 0,
			enabled: !isNil(pollingId),
			refetchInterval: (query) => {
				const { data: promptData } = query.state;

				if (promptData?.status === 'failed') {
					setPollingId(null);

					showNotification({
						title: 'Something went wrong',
						message: 'If the issue persists contact customer support',
						color: 'red',
					});

					onFinishStreaming?.(promptData);
					return false;
				}

				if (
					promptData?.status === 'running' ||
					promptData?.status === 'pending'
				) {
					setActivePrompt(promptData);
				}

				if (
					promptData?.status === 'completed' ||
					promptData?.status === 'stopped'
				) {
					onFinishStreaming?.(promptData);
					setPollingId(null);
					setActivePrompt(null);
					return false;
				}

				return pollingInterval;
			},
		},
	});

	useEffect(() => () => setPollingId(null), []);

	return {
		activePrompt,
		setActivePrompt,
		pollingId,
		setPollingId,
		isStreaming: !isNil(pollingId),
		hasAIGeneratedText: !isNil(activePrompt),
	};
}
